import './Passkey.sass';

import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Header, List, ListItem, ScrollView } from '$uikit';
import { IcClose28 } from '$assets';
import { useTranslator } from '$hooks';
import { Api } from '$api';
import { passkeyActions } from '$store/passkey';
import { toastActions } from '$store/toast';
import { authorizationCode, authorizationDefault } from '$passkey/authorization';
import { register } from '$passkey/ceremony';
import {
  PasskeyCredential,
  PasskeyRedirect,
  apiMessage,
  readCredentials,
  readSuccess,
} from '$passkey/http';

async function loadCredentials(): Promise<PasskeyCredential[]> {
  const data: unknown = await Api.get('/passkey/credentials');
  const list = readCredentials(data);
  if (!list) throw new Error('passkey_invalid');
  return list;
}

export const Passkey: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const reloadId = useRef(0);
  const [credentials, setCredentials] = useState<PasskeyCredential[] | null>(null);
  const ready = credentials !== null;
  const registered = (credentials ?? []).length > 0;
  const [continueRegistration, setContinueRegistration] = useState<
    (() => Promise<void>) | null
  >(null);

  const explain = (error: unknown, fallback: string) => {
    const code = authorizationCode(error);
    if (code) return t(code, { defaultValue: authorizationDefault(code) });
    return apiMessage(error) ?? fallback;
  };

  useEffect(() => {
    const current = ++reloadId.current;
    let ignore = false;
    loadCredentials()
      .then((list) => {
        if (ignore || current !== reloadId.current) return;
        setCredentials(list);
        dispatch(passkeyActions.setRegistered(list.length > 0));
      })
      .catch((error: unknown) => {
        if (ignore || current !== reloadId.current || error instanceof PasskeyRedirect) {
          return;
        }
        const code = authorizationCode(error);
        dispatch(
          toastActions.setToastFail(
            code
              ? t(code, { defaultValue: authorizationDefault(code) })
              : apiMessage(error) ??
                  t('passkey_load_error', {
                    defaultValue: 'Failed to load passkeys',
                  }),
          ),
        );
      });
    return () => {
      ignore = true;
    };
  }, [dispatch, t]);

  const saved = async (replaced: boolean) => {
    setContinueRegistration(null);
    dispatch(
      toastActions.setToastSuccess(
        replaced
          ? t('passkey_replace_success', { defaultValue: 'Passkey replaced' })
          : t('passkey_add_success', { defaultValue: 'Passkey added' }),
      ),
    );
    const current = ++reloadId.current;
    try {
      const list = await loadCredentials();
      if (current !== reloadId.current) return;
      setCredentials(list);
      dispatch(passkeyActions.setRegistered(list.length > 0));
    } catch (error) {
      if (current !== reloadId.current || error instanceof PasskeyRedirect) return;
      dispatch(
        toastActions.setToastFail(
          explain(
            error,
            replaced
              ? t('passkey_replace_error', { defaultValue: 'Failed to replace Passkey' })
              : t('passkey_add_error', { defaultValue: 'Failed to add Passkey' }),
          ),
        ),
      );
    }
  };

  const remove = async () => {
    try {
      const data: unknown = await Api.delete('/passkey/delete');
      if (!readSuccess(data)) return;
      reloadId.current += 1;
      setContinueRegistration(null);
      setCredentials([]);
      dispatch(passkeyActions.setRegistered(false));
      dispatch(
        toastActions.setToastSuccess(
          t('passkey_remove_success', { defaultValue: 'Passkey removed' }),
        ),
      );
    } catch (error) {
      if (error instanceof PasskeyRedirect) return;
      dispatch(
        toastActions.setToastFail(
          explain(
            error,
            t('passkey_remove_error', { defaultValue: 'Failed to remove Passkey' }),
          ),
        ),
      );
    }
  };
  const failed = (error: unknown, replaced: boolean) => {
    setContinueRegistration(null);
    if (error instanceof PasskeyRedirect) return;
    dispatch(
      toastActions.setToastFail(
        explain(
          error,
          replaced
            ? t('passkey_replace_error', { defaultValue: 'Failed to replace Passkey' })
            : t('passkey_add_error', { defaultValue: 'Failed to add Passkey' }),
        ),
      ),
    );
  };

  const add = async () => {
    const replaced = continueRegistration != null || registered;
    try {
      if (continueRegistration) {
        await continueRegistration();
        await saved(true);
        return;
      }
      const registration = await register();
      if (registration.continueRegistration) {
        const next = registration.continueRegistration;
        setContinueRegistration(() => next);
        return;
      }
      await saved(false);
    } catch (error) {
      failed(error, replaced);
    }
  };

  return (
    <>
      <Header showBackButton>
        {t('passkey_manage_title', { defaultValue: 'Passkeys' })}
      </Header>
      <ScrollView>
        <div className="Passkey">
          <p className="Passkey__text">
            {t('passkey_manage_text', {
              defaultValue: 'Sign in with a passkey from this account.',
            })}
          </p>
          {ready && registered ? (
            <List>
              {(credentials ?? []).map((credential) => (
                <ListItem
                  key={credential.id}
                  disableHighlight
                  description={credential.id}
                  after={
                    <span className="Passkey__rowActions">
                      {!continueRegistration ? (
                        <Button size="small" mode="secondary" onClick={add}>
                          {t('passkey_replace_button', { defaultValue: 'Replace' })}
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        mode="secondary"
                        icon={<IcClose28 />}
                        onClick={remove}
                      />
                    </span>
                  }
                >
                  {credential.name || t('passkey_unnamed', { defaultValue: 'Passkey' })}
                </ListItem>
              ))}
            </List>
          ) : null}
          {ready && (!registered || continueRegistration) ? (
            <div className="Passkey__actions">
              {continueRegistration ? (
                <p className="Passkey__text">
                  {t('passkey_replace_ready', {
                    defaultValue: 'Confirmed. Create the new passkey.',
                  })}
                </p>
              ) : null}
              <Button stretched onClick={add}>
                {continueRegistration
                  ? t('passkey_replace_create', { defaultValue: 'Create Passkey' })
                  : t('passkey_add_button', { defaultValue: 'Add Passkey' })}
              </Button>
            </div>
          ) : null}
        </div>
      </ScrollView>
    </>
  );
};
